import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { usePermission } from '@saas-kit/lib';
import { ErrorBanner, Field, Input, Skeleton, Textarea } from '@saas-kit/ui';

import {
  listPermissions,
  listRolePermissions,
  type PermissionRow,
  type Role,
} from './api';
import { PermissionGrid } from './PermissionGrid';
import { RoleEditorFooter } from './RoleEditorFooter';
import { RoleEditorHeader } from './RoleEditorHeader';
import { useRoleEditorSave } from './useRoleEditorSave';

interface Props {
  mode: 'create' | 'edit' | 'empty';
  role: Role | null;
  onSaved: (role: Role) => void;
  onDeleted: () => void;
  onCancelCreate: () => void;
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

export function RoleEditor({
  mode,
  role,
  onSaved,
  onDeleted,
  onCancelCreate,
}: Props): JSX.Element {
  const { t } = useTranslation('rbac');
  const canManage = usePermission('members.manage');

  const isSystem = role?.is_system ?? false;
  const editable = canManage && !isSystem;

  const [name, setName] = useState(role?.name ?? '');
  const [slug, setSlug] = useState(role?.slug ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    setName(role?.name ?? '');
    setSlug(role?.slug ?? '');
    setDescription(role?.description ?? '');
    setSlugTouched(false);
  }, [role?.description, role?.id, role?.name, role?.slug]);

  const allPerms = useQuery<PermissionRow[]>({
    queryKey: ['tenant-permissions'],
    queryFn: listPermissions,
    staleTime: 60_000,
  });
  const rolePerms = useQuery<PermissionRow[]>({
    queryKey: ['tenant-role-permissions', role?.id ?? 'none'],
    queryFn: () => listRolePermissions(role!.id),
    enabled: mode === 'edit' && Boolean(role),
  });

  const baseline = useMemo(
    () => new Set((rolePerms.data ?? []).map((permission) => permission.key)),
    [rolePerms.data],
  );

  useEffect(() => {
    if (mode === 'edit' && rolePerms.data) setPicked(new Set(baseline));
    if (mode === 'create') setPicked(new Set());
  }, [baseline, mode, rolePerms.data]);

  const { save, isPending, savedAt, errorKey, setErrorKey } = useRoleEditorSave({
    mode,
    role,
    onSaved,
  });

  if (mode === 'empty') {
    return (
      <div className="grid h-full place-items-center rounded-lg border border-dashed border-border bg-card p-10 text-center">
        <p className="text-sm text-muted-foreground">{t('editor.no_selection')}</p>
      </div>
    );
  }

  const slugValue = slugTouched ? slug : mode === 'create' ? slugify(name) : slug;
  const normalizedSlug = slugValue.trim().toLowerCase();
  const validSlug = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalizedSlug);
  const dirty =
    name !== (role?.name ?? '') ||
    normalizedSlug !== (role?.slug ?? '') ||
    (description ?? '') !== (role?.description ?? '') ||
    picked.size !== baseline.size ||
    [...picked].some((key) => !baseline.has(key));

  const onSave = (): void =>
    save({
      name: name.trim(),
      slug: normalizedSlug,
      description: description.trim() ? description.trim() : null,
      desired: picked,
      baseline,
    });

  return (
    <section className="flex h-full min-h-0 flex-col gap-4 rounded-lg border border-border bg-card p-5">
      <RoleEditorHeader
        isCreate={mode === 'create'}
        isSystem={isSystem}
        canManage={canManage}
        role={role}
        onDeleted={onDeleted}
        onError={setErrorKey}
      />

      {errorKey ? <ErrorBanner>{t(errorKey, errorKey)}</ErrorBanner> : null}

      <div className="grid gap-3 md:grid-cols-2">
        <Field label={t('editor.name_label')} htmlFor="role-name">
          <Input
            id="role-name"
            value={name}
            onChange={(event) => {
              const next = event.target.value;
              setName(next);
              if (!slugTouched && mode === 'create') setSlug(slugify(next));
            }}
            disabled={!editable}
            placeholder={t('editor.name_placeholder')}
          />
        </Field>
        <Field label={t('editor.slug_label')} htmlFor="role-slug">
          <Input
            id="role-slug"
            value={slugValue}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value);
            }}
            disabled={!editable}
            placeholder={t('editor.slug_placeholder')}
          />
          {!validSlug ? (
            <p className="mt-1 text-xs text-destructive">{t('validation.slug_invalid')}</p>
          ) : null}
        </Field>
        <div className="md:col-span-2">
          <Field label={t('editor.description_label')} htmlFor="role-description">
            <Textarea
              id="role-description"
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={!editable}
              placeholder={t('editor.description_placeholder')}
            />
          </Field>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{t('perms.section_title')}</h3>
          <p className="text-xs text-muted-foreground">{t('perms.section_subtitle')}</p>
        </div>
        {allPerms.isLoading || (mode === 'edit' && rolePerms.isLoading) ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <PermissionGrid
            all={(allPerms.data ?? []).filter((permission) => permission.assignable)}
            picked={picked}
            disabled={!editable}
            onToggle={(permissionKey, on) =>
              setPicked((previous) => {
                const next = new Set(previous);
                if (on) next.add(permissionKey);
                else next.delete(permissionKey);
                return next;
              })
            }
          />
        )}
      </div>

      <RoleEditorFooter
        isCreate={mode === 'create'}
        editable={editable}
        isPending={isPending}
        showSaved={savedAt > 0 && !dirty && !isPending}
        canSave={dirty && name.trim().length > 0 && normalizedSlug.length > 0 && validSlug}
        onSave={onSave}
        onCancelCreate={onCancelCreate}
      />
    </section>
  );
}
